import React, { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { Input, CardInfo, Spacer } from '@telus/koodo-ui-kit'
import BodySmall from '@apps/components/Typography/BodySmall'
import BodySmallBold from '@apps/components/Typography/BodySmallBold'
import BodyError from '@apps/components/Typography/BodyError'
import Heading3 from '@apps/components/Typography/Heading3'
import {
  ACCOUNT_SETUP_STATE,
  updateAccountSetupState
} from '@apps/redux/features/AccountSetupSlice'
import { useDispatch, useSelector } from 'react-redux'
import AccountSetupChangeHandlers, {
  ACCOUNT_SETUP_EVENTS
} from '@apps/utils/ChangeHandlers/AccountSetupChangeHandlers'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { getShortTextFromPage, getLongTextFromPage, getById } from '@apps/utils/contentful'
import Sites from '@sites/index'

// Renders the PIN subtitle body from a single long text field so business can add,
// remove or reorder bullets - and add trailing copy outside the list - without a code change.
const MarkdownList = ({ children }) => (
  <ul className="list-disc list-outside pl-6 text-black">{children}</ul>
)

MarkdownList.propTypes = {
  children: PropTypes.node
}

// marker:text-black keeps the bullet glyphs black regardless of theme, matching the
// previous markup where the black `ul` supplied the marker colour and the text was text-copy.
const MarkdownListItem = ({ children }) => (
  <li className="text-copy leading-5 marker:text-black">{children}</li>
)

MarkdownListItem.propTypes = {
  children: PropTypes.node
}

const MarkdownParagraph = ({ children }) => <p className="m-0 leading-5">{children}</p>

MarkdownParagraph.propTypes = {
  children: PropTypes.node
}

const createPinStepsMarkdownComponents = {
  ul: MarkdownList,
  li: MarkdownListItem,
  p: MarkdownParagraph
}

const PIN = ({ pinRef, handleBlurPin, handleBlurConfirmPin }) => {
  const dispatch = useDispatch()
  const { pageContent } = useSelector((state) => state.common.content)
  const { userType } = useSelector((state) => state.common)
  const accountSetupState = useSelector((state) => state.accountSetupPage)

  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: language } = useContext(ApplicationContext)

  const {
    [ACCOUNT_SETUP_STATE.PIN]: pin,
    [ACCOUNT_SETUP_STATE.CONFIRM_PIN]: confirmPin,
    [ACCOUNT_SETUP_STATE.PIN_ERRORS]: {
      [ACCOUNT_SETUP_STATE.IS_INVALID_PIN_LENGTH]: isInvalidPINLength,
      [ACCOUNT_SETUP_STATE.PIN_STARTS_WITH_ZERO]: pinStartsWithZero,
      [ACCOUNT_SETUP_STATE.PIN_ONE_TWO_THREE_FOUR]: pinOneTwoThreeFour,
      [ACCOUNT_SETUP_STATE.PIN_SINGLE_REPEATING_DIGIT]: pinSingleRepeatingDigit,
      [ACCOUNT_SETUP_STATE.SHOW_REQUIREMENTS]: showRequirements,
      [ACCOUNT_SETUP_STATE.PIN_MATCHES]: pinMatches,
      [ACCOUNT_SETUP_STATE.CONFIRM_PIN_REQUIRED]: confirmPinRequired,
      [ACCOUNT_SETUP_STATE.PIN_REQUIRED]: pinRequired
    }
  } = accountSetupState
  
  const PIN_LENGTH = 4

  const text = useMemo(() => {
    if (!pageContent || !language || !userType) return {}
    const contentSuffix = userType === Sites.dealer ? 'Dealer' : 'Consumer'
    const accountSetupPageContent = getById(pageContent, `accountSetupPage${contentSuffix}`)?.[
      language
    ]
    return {
      confirmPinLabel: getShortTextFromPage(accountSetupPageContent, 'confirmPinLabel'),
      createPinSteps: getLongTextFromPage(accountSetupPageContent, 'createPinSteps'),
      pinErrorDigits: getShortTextFromPage(accountSetupPageContent, 'pinErrorDigits'),
      pinErrorMismatch: getShortTextFromPage(accountSetupPageContent, 'pinErrorMismatch'),
      pinErrorOneNumber: getShortTextFromPage(accountSetupPageContent, 'pinErrorOneNumber'),
      pinErrorRequired: getShortTextFromPage(accountSetupPageContent, 'pinErrorRequired'),
      pinErrorStartsZero: getShortTextFromPage(accountSetupPageContent, 'pinErrorStartsZero'),
      pinLabel: getShortTextFromPage(accountSetupPageContent, 'pinLabel'),
      pinRequirementsTitle: getShortTextFromPage(accountSetupPageContent, 'pinRequirementsTitle'),
      pinSubtitle: getShortTextFromPage(accountSetupPageContent, 'pinSubtitle'),
      pinTitle: getShortTextFromPage(accountSetupPageContent, 'pinTitle')
    }
  }, [pageContent, language, userType])

  const {
    confirmPinLabel,
    createPinSteps,
    pinErrorDigits,
    pinErrorMismatch,
    pinErrorOneNumber,
    pinErrorRequired,
    pinErrorStartsZero,
    pinLabel,
    pinRequirementsTitle,
    pinSubtitle,
    pinTitle
  } = text

  const confirmPINErrorMessage = () => {
    if (confirmPinRequired) {
      return pinErrorRequired
    }

    if (pinMatches) {
      return pinErrorMismatch
    }

    return ''
  }

  const pinErrorState = () => {
    if (
      pinRequired ||
      isInvalidPINLength ||
      pinStartsWithZero ||
      pinOneTwoThreeFour ||
      pinSingleRepeatingDigit
    ) {
      return true
    }
    return false
  }

  const confirmPinErrorState = () => {
    if (confirmPinRequired || pinMatches) {
      return true
    }
    return false
  }

  const setAccountSetupState = (payload) => {
    dispatch(updateAccountSetupState(payload))
  }

  const handlePinFocus = () => {
    AccountSetupChangeHandlers[ACCOUNT_SETUP_EVENTS.PIN].onFocus(
      accountSetupState,
      setAccountSetupState
    )
  }

  const handlePinChange = (value) => {
    AccountSetupChangeHandlers[ACCOUNT_SETUP_EVENTS.PIN].onChange(
      value,
      setAccountSetupState
    )
  }

  const handleConfirmPinChange = (value) => {
    AccountSetupChangeHandlers[ACCOUNT_SETUP_EVENTS.CONFIRM_PIN].onChange(
      value,
      setAccountSetupState
    )
  }

  return (
    <div>
      <Heading3
        className="mt-0"
        tokens={{ fontSize: 20, lineHeight: 28 / 20 }}
      >
        {pinTitle}
      </Heading3>
      <Spacer space={3} />
      <CardInfo
        heading={<BodySmallBold className="leading-5">{pinSubtitle}</BodySmallBold>}
        tokens={{ paddingTop: 12, paddingBottom: 12 }}
      >
        <ReactMarkdown
          className="text-xs font-body text-copy space-y-3"
          components={createPinStepsMarkdownComponents}
          skipHtml
        >
          {createPinSteps || ''}
        </ReactMarkdown>
      </CardInfo>
      <Spacer space={4} />
      <div ref={pinRef}>
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-6 lg:space-y-0">
          <Input
            type="password"
            inputMode="numeric"
            pattern="[0-9]{4}"
            label={pinLabel}
            onFocus={handlePinFocus}
            onBlur={handleBlurPin}
            onChange={handlePinChange}
            maxLength={PIN_LENGTH}
            feedback={pinRequired ? pinErrorRequired : undefined}
            validation={pinErrorState() ? 'error' : undefined}
            value={pin}
            data-testid="accountSetup-pin"
          />
          <Input
            type="password"
            inputMode="numeric"
            pattern="[0-9]{4}"
            label={confirmPinLabel}
            onBlur={handleBlurConfirmPin}
            onChange={handleConfirmPinChange}
            maxLength={PIN_LENGTH}
            feedback={confirmPINErrorMessage() || undefined}
            validation={confirmPinErrorState() ? 'error' : undefined}
            value={confirmPin}
            data-testid="accountSetup-confirmPin"
          />
        </div>
        <Spacer space={4} />
        <CardInfo
          heading={<BodySmallBold className="leading-5">{pinRequirementsTitle}</BodySmallBold>}
          tokens={{ paddingTop: 12, paddingBottom: 12 }}
        >
          <ul className="list-disc list-outside pl-6 text-black">
            <li>
              {showRequirements && isInvalidPINLength ? (
                <BodyError className="inline font-body-small tracking-wider">
                  {pinErrorDigits}
                </BodyError>
              ) : (
                <BodySmall className="inline leading-5">{pinErrorDigits}</BodySmall>
              )}
            </li>
            <li>
              {showRequirements && (pinStartsWithZero || pinOneTwoThreeFour) ? (
                <BodyError className="inline font-body-small tracking-wider">
                  {pinErrorStartsZero}
                </BodyError>
              ) : (
                <BodySmall className="inline leading-5">{pinErrorStartsZero}</BodySmall>
              )}
            </li>
            <li>
              {showRequirements && pinSingleRepeatingDigit ? (
                <BodyError className="inline font-body-small tracking-wider">
                  {pinErrorOneNumber}
                </BodyError>
              ) : (
                <BodySmall className="inline leading-5">{pinErrorOneNumber}</BodySmall>
              )}
            </li>
          </ul>
        </CardInfo>
      </div>
    </div>
  )
}

export default PIN
